import { useEffect } from 'react';
import { useUI } from '@/contexts/UIContext';
import { Sidebar } from '@/layouts/shell/Sidebar';
import { cn } from '@/lib/cn';
import { XMarkIcon } from '@/icons';

/**
 * Off-canvas navigation drawer for < lg viewports. A backdrop + sliding panel
 * that traps nothing heavier than needed: Esc closes, backdrop closes, and
 * choosing a link closes it. The desktop sidebar is hidden at these sizes.
 */
export function MobileNav() {
  const { mobileNavOpen, setMobileNavOpen } = useUI();

  // Close on Escape.
  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMobileNavOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileNavOpen, setMobileNavOpen]);

  return (
    <div className={cn('fixed inset-0 z-50 lg:hidden', !mobileNavOpen && 'pointer-events-none')} aria-hidden={!mobileNavOpen}>
      {/* Backdrop */}
      <div
        className={cn('absolute inset-0 bg-black/50 transition-opacity', mobileNavOpen ? 'opacity-100' : 'opacity-0')}
        onClick={() => setMobileNavOpen(false)}
      />
      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className={cn(
          'absolute inset-y-0 left-0 w-72 max-w-[80%] border-r border-border bg-surface shadow-xl transition-transform duration-200',
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <button
          type="button"
          onClick={() => setMobileNavOpen(false)}
          className="absolute right-3 top-4 z-10 rounded-lg p-1 text-fg-muted hover:bg-surface-raised hover:text-fg"
          aria-label="Close navigation menu"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
        <Sidebar forceExpanded onNavigate={() => setMobileNavOpen(false)} />
      </div>
    </div>
  );
}
